/**
 * AppBridgeModal Component (Route-Based)
 *
 * Renders an App Bridge v4 modal using the <ui-modal> web component.
 * This component creates a modal that renders outside the app iframe,
 * providing a native Shopify admin experience with proper overlay.
 *
 * @example
 * ```tsx
 * const modal = useModalRoute('/app/modal/alert-detail');
 * 
 * <AppBridgeModal
 *   src={modal.src}
 *   open={modal.isOpen}
 *   onClose={modal.close}
 *   variant="large"
 * />
 * ```
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import type { ModalMessage } from '~/hooks/core/useModalCommunication';
import { logger } from '~/utils/client-logger';

/**
 * BroadcastChannel name for modal communication
 * Must match the name in useModalCommunication.ts
 */
const MODAL_CHANNEL_NAME = 'stock-sentry-modal-channel';

// Counter for generating unique modal IDs (client-side only)
let modalCounter = 0;

// Type declarations for ui-modal and ui-title-bar are in app/types/app-bridge.d.ts

export type ModalVariant = 'small' | 'base' | 'large' | 'max';

export interface AppBridgeModalProps {
  /** Unique modal ID for App Bridge API (optional - will be auto-generated if not provided) */
  id?: string;
  /** Modal source URL (from useModalRoute.src) */
  src: string | null;
  /** Whether the modal is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Modal size variant */
  variant?: ModalVariant;
  /** Message handler for postMessage from modal */
  onMessage?: (message: ModalMessage) => void;
  /** Title displayed in the native modal title bar (with close button) */
  title?: string;
}

/**
 * Route-based App Bridge modal component
 * Uses <ui-modal> web component with src attribute for iframe content
 */
export function AppBridgeModal({
  id,
  src,
  open,
  onClose,
  variant = 'large',
  onMessage,
  title,
}: AppBridgeModalProps) {
  const modalRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const onMessageRef = useRef(onMessage);

  // Track the last valid src to keep modal mounted during close animation
  const lastSrcRef = useRef<string | null>(null);

  // Generate a stable modal ID using a counter to avoid hydration mismatch
  // Server renders with a placeholder, client uses incrementing counter
  const [modalId] = useState(() => {
    if (id) return id;
    // Use counter only on client side, this runs only once per component instance
    if (typeof window !== 'undefined') {
      modalCounter += 1;
      return `modal-${modalCounter}`;
    }
    return 'modal-ssr'; // SSR placeholder (should never be used in practice)
  });

  // Update lastSrcRef when we have a valid src
  if (src) {
    lastSrcRef.current = src;
  }

  // Use current src if available, otherwise use last valid src for close animation
  // IMPORTANT: We keep using lastSrcRef even after close to keep the modal mounted
  // This prevents the ui-modal element from being unmounted and losing its ref
  const effectiveSrc = src || lastSrcRef.current;

  // Append modalId to src URL so the modal content can use shopify.modal.hide()
  const srcWithId = effectiveSrc ? (
    effectiveSrc.includes('?')
      ? `${effectiveSrc}&modalId=${encodeURIComponent(modalId)}`
      : `${effectiveSrc}?modalId=${encodeURIComponent(modalId)}`
  ) : null;

  // Keep refs updated
  useEffect(() => {
    onCloseRef.current = onClose;
    onMessageRef.current = onMessage;
  }, [onClose, onMessage]);

  // Handle modal hide event
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const handleHide = () => {
      onCloseRef.current();
    };

    modal.addEventListener('hide', handleHide);
    return () => modal.removeEventListener('hide', handleHide);
  }, []);

  // Handle messages from modal iframe via BroadcastChannel (primary for App Bridge v4)
  useEffect(() => {
    if (!open) return;
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel(MODAL_CHANNEL_NAME);

    channel.onmessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object' || typeof event.data.type !== 'string') {
        return;
      }

      const message = event.data as ModalMessage;
      logger.debug('AppBridgeModal', 'BroadcastChannel message received', message);

      // Handle close message
      if (message.type === 'modal-close') {
        onCloseRef.current();
        return;
      }

      // Pass to handler
      onMessageRef.current?.(message);
    };

    return () => channel.close();
  }, [open]);

  // Handle postMessage from modal iframe (fallback for non-BroadcastChannel contexts)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // In Shopify embedded app context, the modal iframe may have a different
      // effective origin due to App Bridge proxying. We validate the message
      // structure instead of relying solely on origin check.
      // Only accept messages that have the expected structure for our modal protocol.
      if (!event.data || typeof event.data !== 'object' || typeof event.data.type !== 'string') {
        return;
      }

      const message = event.data as ModalMessage;

      // Handle close message
      if (message.type === 'modal-close') {
        onCloseRef.current();
        return;
      }

      // Pass to handler
      onMessageRef.current?.(message);
    };

    if (open) {
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }
  }, [open]);

  // Control modal visibility
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    if (open && srcWithId) {
      // Show modal
      if (typeof (modal as any).show === 'function') {
        (modal as any).show();
      }
    } else if (!open) {
      // Hide modal - keep lastSrcRef so the modal element stays mounted
      // This is critical for being able to reopen the modal
      if (typeof (modal as any).hide === 'function') {
        (modal as any).hide();
      }
      // NOTE: We intentionally do NOT clear lastSrcRef here.
      // Clearing it would unmount the ui-modal element, which breaks reopening.
      // The lastSrcRef will be updated with a new URL when the modal opens again.
    }
  }, [open, srcWithId]);

  // Handle dynamic variant changes
  // The ui-modal web component requires setting the property, not just the attribute
  // Per Shopify docs: modal.variant = 'large' (property setter)
  // Note: Can only change between small, base, and large while modal is open
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal || !open) return;

    // Use property setter for dynamic variant changes (not setAttribute)
    // This triggers the ui-modal's internal resize logic
    (modal as any).variant = variant;
  }, [variant, open]);

  // Don't render if no src
  if (!srcWithId) return null;

  return (
    <ui-modal
      ref={modalRef}
      id={modalId}
      src={srcWithId}
      variant={variant}
    >
      {title && <ui-title-bar title={title} />}
    </ui-modal>
  );
}

/**
 * Inline modal variant for simple content (non-route-based)
 * Use this for modals that don't need data fetching
 */
export interface AppBridgeInlineModalProps {
  /** Unique modal ID */
  id: string;
  /** Whether the modal is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Modal size variant */
  variant?: ModalVariant;
  /** Modal content */
  children: React.ReactNode;
  /** Primary action button */
  primaryAction?: {
    content: string;
    onAction: () => void;
    loading?: boolean;
    disabled?: boolean;
    destructive?: boolean;
  };
  /** Secondary action buttons */
  secondaryActions?: Array<{
    content: string;
    onAction: () => void;
  }>;
}

/**
 * Inline App Bridge modal for simple content
 * Note: This uses HTML children approach - has limitations with custom JS
 */
export function AppBridgeInlineModal({
  id,
  open,
  onClose,
  title,
  variant = 'base',
  children,
  primaryAction,
  secondaryActions,
}: AppBridgeInlineModalProps) {
  const modalRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const handleHide = () => onCloseRef.current();
    modal.addEventListener('hide', handleHide);
    return () => modal.removeEventListener('hide', handleHide);
  }, []);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    if (open) {
      if (typeof (modal as any).show === 'function') {
        (modal as any).show();
      }
    } else {
      if (typeof (modal as any).hide === 'function') {
        (modal as any).hide();
      }
    }
  }, [open]);

  if (!open) return null;

  return (
    <ui-modal ref={modalRef} id={id} variant={variant}>
      <ui-title-bar title={title}>
        {secondaryActions?.map((action, index) => (
          <button key={index} onClick={action.onAction}>
            {action.content}
          </button>
        ))}
        {primaryAction && (
          <button
            onClick={primaryAction.onAction}
            disabled={primaryAction.disabled || primaryAction.loading}
          >
            {primaryAction.loading ? 'Loading...' : primaryAction.content}
          </button>
        )}
      </ui-title-bar>
      <div style={{ padding: '16px' }}>
        {children}
      </div>
    </ui-modal>
  );
}

export default AppBridgeModal;
